import { motion, useAnimation, Transition, Variants } from "motion/react"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { TAnimatedSvgIconProps, TAnimatedSvgIconSize } from "./IconType"

const defaultTransition: Transition = {
  duration: 0.5,
  opacity: { duration: 0.2 },
}

const bubbleVariants: Variants = {
  normal: { scale: 1, rotate: 0 },
  animate: { scale: [1, 1.06, 1], rotate: [0, -3, 0] },
}

const dotVariants: Variants = {
  normal: { opacity: 1, y: 0 },
  animate: { opacity: [1, 0.3, 1], y: [0, -1.5, 0] },
}

/** A speech bubble with three typing dots — the comment moderation nav icon. */
const MessageSquareIcon = forwardRef<
  TAnimatedSvgIconProps,
  TAnimatedSvgIconSize
>(({ onMouseEnter, onMouseLeave, className, size = 24, ...props }, ref) => {
  const controls = useAnimation()
  const isControlledRef = useRef(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useImperativeHandle(ref, () => {
    isControlledRef.current = true

    return {
      startAnimation: () => {
        setIsAnimating(true)
        void controls.start("animate")
      },
      stopAnimation: () => {
        setIsAnimating(false)
        void controls.start("normal")
      },
    }
  })

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isControlledRef.current) {
        setIsAnimating(true)
        void controls.start("animate")
      } else {
        onMouseEnter?.(e)
      }
    },
    [controls, onMouseEnter],
  )

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isControlledRef.current) {
        setIsAnimating(false)
        void controls.start("normal")
      } else {
        onMouseLeave?.(e)
      }
    },
    [controls, onMouseLeave],
  )

  return (
    <div
      className={cn(className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <motion.path
          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
          variants={bubbleVariants}
          transition={defaultTransition}
          animate={controls}
        />
        {[8, 12, 16].map((cx, i) => (
          <motion.circle
            key={cx}
            cx={cx}
            cy={10}
            r={0.9}
            fill="currentColor"
            stroke="none"
            variants={dotVariants}
            transition={{
              duration: 0.6,
              repeat: isAnimating ? Infinity : 0,
              delay: i * 0.15,
            }}
            animate={controls}
          />
        ))}
      </svg>
    </div>
  )
})

MessageSquareIcon.displayName = "MessageSquareIcon"

export { MessageSquareIcon }
